import { FlexColumn, FlexRow } from "Components/Flex";
import { useMobileMediaQuery } from "hooks";
import { useMenuBarHeight } from "hooks/useMenuBarHeight";
import { H3, H4, Body2 } from "melodies-source/Text";
import { PropsWithChildren, ReactNode, useEffect, useState } from "react";
import { AnimatedCaret } from "Routes/styled";
import styled from "styled-components";

interface ContainerProps {
  menuBarHeight: number;
}

const Container = styled.div<ContainerProps>`
  --x-padding: 30px;
  --y-padding: 20px;
  --icon-size: 20px;
  --icon-space: 10px;

  &:not(:first-child) {
    border-top: 1px solid #e6e9eb;
  }

  ${({ theme }) => theme.media.mobile} {
    --x-padding: 20px;
    scroll-margin-top: ${({ menuBarHeight }) => menuBarHeight}px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderContainer = styled(FlexColumn)<{ onClick: () => void }>`
  padding: var(--y-padding) var(--x-padding);
  gap: 5px;
  ${({ onClick }) => onClick && "cursor: pointer;"}
`;

const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--icon-size);
  height: var(--icon-size);
  margin-right: var(--icon-space);
`;

const Actions = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: var(--x-padding);
`;

const Content = styled.div`
  position: relative;
  padding-left: calc(var(--x-padding) + var(--icon-size) + var(--icon-space));
  padding-right: var(--x-padding);
  padding-bottom: var(--x-padding);

  ${(p) => p.theme.media.mobile} {
    padding: 0 20px 20px 20px;
  }
`;

export const Title: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const isMobile = useMobileMediaQuery();

  const Heading = isMobile ? H4 : H3;
  return <Heading>{children}</Heading>;
};

interface SectionProps {
  title: ReactNode;
  icon: ReactNode;
  open?: boolean;
  onToggle?: () => void;
  className?: string;
  id?: string;
  hasChanges?: boolean;
}

export const Section = ({
  title,
  icon,
  open,
  onToggle,
  children,
  className,
  id,
  hasChanges,
}: PropsWithChildren<SectionProps>) => {
  const { menuBarHeight } = useMenuBarHeight();

  return (
    <Container id={id} className={className} menuBarHeight={menuBarHeight}>
      <HeaderContainer onClick={onToggle}>
        <Header>
          <Icon>{icon}</Icon>
          {typeof title === "string" ? <Title>{title}</Title> : title}
          {onToggle && (
            <Actions>
              <AnimatedCaret
                direction={open ? "up" : "down"}
                $withTransition={false}
              />
            </Actions>
          )}
        </Header>
        {hasChanges && (
          <FlexRow>
            <UnsavedChangesStatus />
          </FlexRow>
        )}
      </HeaderContainer>
      {open && <Content>{children}</Content>}
    </Container>
  );
};

export const UnsavedChangesStatus = ({ className }: { className?: string }) => {
  return (
    <FlexRow yCenter className={className}>
      <Body2 style={{ color: "var(--main-color)" }}>
        This section has unsaved changes
      </Body2>
    </FlexRow>
  );
};
